import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import {
  Users, CreditCard, MessageSquare, Flag, TrendingUp, DollarSign,
  Calendar, MessagesSquare, Bell, GraduationCap, MapPin, UserCheck,
  ShieldCheck, ShieldAlert, RefreshCw, HardDrive, FlaskConical, Inbox, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { tierLabel } from '@/lib/tiers';

const TIER_COLORS = {
  // New 5-tier model
  open_play: '#94a3b8',
  verified_player: '#56dfb4',
  instructor_starter: '#cbd5e1',
  instructor_plus: '#56c6df',
  instructor_unlimited: '#b456df',
  // Legacy (only render until migration completes)
  free: '#94a3b8',
  monthly: '#56c6df',
  annual: '#56dfb4',
  lifetime: '#b456df',
};

function GridfsHealthBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/system/gridfs-health')
      .then(({ data }) => setData(data))
      .catch(() => setData({ status: 'down', error: true }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const ok = data?.status === 'active';
  const Icon = ok ? ShieldCheck : ShieldAlert;
  const tone = ok
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-amber-200 bg-amber-50 text-amber-700';

  const counts = data?.checks?.counts;
  const newest = data?.checks?.timestamps?.newest_upload;
  const writeOk = data?.checks?.write_probe?.ok;

  return (
    <Card className={`border ${tone} mb-8`} data-testid="admin-gridfs-health-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em]">
                Image Persistence (GridFS) — {ok ? 'Active' : 'Needs Attention'}
              </p>
              {data ? (
                <>
                  <p className="text-xs mt-1 opacity-80">
                    {counts?.fs_files ?? '?'} files · {counts?.total_mb ?? '?'} MB ·
                    {' '}live write probe {writeOk ? '✓ passed' : '✗ failed'}
                    {newest ? ` · last upload ${new Date(newest).toLocaleString()}` : ''}
                  </p>
                  {data.checked_at && (
                    <p className="text-[11px] mt-1 opacity-60" data-testid="admin-gridfs-checked-at">
                      Checked at {new Date(data.checked_at).toLocaleTimeString()}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs mt-1 opacity-80">Checking…</p>
              )}
              <p className="text-[11px] mt-2 opacity-70">
                Hit <strong>Re-check</strong> any time to run a fresh live probe.
                Numbers only change when someone uploads a photo — stable
                counts between checks mean images aren't being lost, which is
                exactly what you want.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs rounded-full px-3 py-1.5 bg-white/70 hover:bg-white inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-gridfs-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Re-check
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function S3HealthBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/system/s3-health')
      .then(({ data }) => setData(data))
      .catch(() => setData({ ok: false, error: 'request failed' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const ok = data?.ok === true;
  const Icon = ok ? ShieldCheck : ShieldAlert;
  const tone = ok
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-amber-200 bg-amber-50 text-amber-700';

  return (
    <Card className={`border ${tone} mb-8`} data-testid="admin-s3-health-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em]">
                Image Storage (AWS S3) — {ok ? 'Active' : 'Needs Attention'}
              </p>
              {data ? (
                <>
                  <p className="text-xs mt-1 opacity-80">
                    Bucket <code className="bg-white/60 px-1 rounded">{data.bucket || '—'}</code>
                    {' '}· region <code className="bg-white/60 px-1 rounded">{data.region || '—'}</code>
                    {' '}· live write/head/delete probe {ok ? '✓ passed' : '✗ failed'}
                  </p>
                  {data.error && (
                    <p className="text-[11px] mt-1 font-mono text-red-700 break-words" data-testid="admin-s3-health-error">
                      {data.error}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs mt-1 opacity-80">Checking…</p>
              )}
              <p className="text-[11px] mt-2 opacity-70">
                New uploads write to S3; legacy GridFS files still resolve via
                fallback in the serve endpoint. If this turns amber, image
                uploads will fail — check AWS console or rotate credentials.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs rounded-full px-3 py-1.5 bg-white/70 hover:bg-white inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-s3-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Re-check
          </button>
        </div>
      </CardContent>
    </Card>
  );
}


function DeploySmokeBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = () => {
    setLoading(true);
    api.get('/admin/system/deploy-smoke')
      .then(({ data }) => setData(data))
      .catch((e) => setData({ status: 'fail', error: e?.message || 'request failed' }))
      .finally(() => setLoading(false));
  };

  const allOk = data?.status === 'pass';
  const tone = !data
    ? 'border-slate-200 bg-slate-50 text-slate-600'
    : allOk
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-rose-200 bg-rose-50 text-rose-700';

  const labels = {
    mongo_ping: 'Mongo',
    gridfs_write: 'GridFS write',
    resend: 'Resend',
    twilio: 'Twilio',
    paypal: 'PayPal',
  };

  return (
    <Card className={`border ${tone} mb-8`} data-testid="admin-deploy-smoke-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
              <HardDrive className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em]">
                Post-Deploy Smoke Test {data ? `— ${allOk ? 'All Green' : 'Failures Detected'}` : ''}
              </p>
              <p className="text-[11px] mt-1 opacity-70">
                One-click validation of Mongo, GridFS, Resend, Twilio &amp; PayPal credentials.
                Run this once after every deploy.
              </p>
              {data?.checks ? (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {Object.keys(labels).map(k => {
                    const c = data.checks[k];
                    if (!c) return null;
                    return (
                      <span
                        key={k}
                        className={`text-[11px] px-2 py-0.5 rounded-full ${
                          c.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                        }`}
                        title={c.detail || ''}
                        data-testid={`smoke-${k}`}
                      >
                        {c.ok ? '✓' : '✗'} {labels[k]}
                      </span>
                    );
                  })}
                  {data.elapsed_ms ? (
                    <span className="text-[11px] text-slate-500 px-2 py-0.5">
                      {data.elapsed_ms}ms
                    </span>
                  ) : null}
                </div>
              ) : null}
              {data && !allOk ? (
                <ul className="mt-2 text-[11px] space-y-0.5">
                  {Object.entries(data.checks || {}).filter(([, c]) => !c.ok).map(([k, c]) => (
                    <li key={k}><strong>{labels[k] || k}:</strong> {c.detail || 'failed'}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
          <button
            onClick={run}
            disabled={loading}
            className="text-xs rounded-full px-3 py-1.5 bg-white/70 hover:bg-white inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-deploy-smoke-run-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            {data ? 'Re-run' : 'Run smoke test'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function CronHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingDaily, setTestingDaily] = useState(false);
  const [testResultDaily, setTestResultDaily] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/cron-health')
      .then(({ data }) => setData(data))
      .catch(() => setData({ crons: [], error: true }))
      .finally(() => setLoading(false));
  };

  const sendTestSnapshot = () => {
    setTesting(true);
    setTestResult(null);
    api.post('/admin/test-weekly-snapshot')
      .then(({ data }) => setTestResult({ ok: true, ...data }))
      .catch((err) => setTestResult({ ok: false, error: err?.response?.data?.detail || 'Request failed' }))
      .finally(() => setTesting(false));
  };

  const sendTestDailyAlert = () => {
    setTestingDaily(true);
    setTestResultDaily(null);
    api.post('/admin/test-daily-alerts')
      .then(({ data }) => setTestResultDaily({ ok: true, ...data }))
      .catch((err) => setTestResultDaily({ ok: false, error: err?.response?.data?.detail || 'Request failed' }))
      .finally(() => setTestingDaily(false));
  };

  useEffect(() => { load(); }, []);

  const crons = data?.crons || [];
  // Worst-case tone drives the card border so a single stale cron is
  // immediately visible without expanding the card.
  const worst = crons.reduce((acc, c) => {
    const rank = { red: 3, amber: 2, never: 1, fresh: 0 }[c.status] ?? 0;
    return rank > acc ? rank : acc;
  }, 0);
  const tone =
    !data ? 'border-slate-200' :
    worst >= 3 ? 'border-red-200 bg-red-50/40' :
    worst >= 2 ? 'border-amber-200 bg-amber-50/30' :
    worst >= 1 ? 'border-slate-200 bg-slate-50/40' :
    'border-emerald-200 bg-emerald-50/30';

  const pillFor = (status) => {
    if (status === 'red') return 'bg-red-100 text-red-700 border-red-200';
    if (status === 'amber') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (status === 'never') return 'bg-slate-100 text-slate-500 border-slate-200';
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  };

  const formatLastRun = (iso, hoursSince) => {
    if (!iso) return 'never';
    if (hoursSince == null) return new Date(iso).toLocaleString();
    if (hoursSince < 1) return `${Math.round(hoursSince * 60)} min ago`;
    if (hoursSince < 48) return `${hoursSince.toFixed(1)} hr ago`;
    return `${Math.round(hoursSince / 24)} days ago`;
  };

  return (
    <Card className={`border ${tone} mb-8`} data-testid="admin-cron-health-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <RefreshCw className="w-4 h-4 text-[#56c6df]" /> Cron Health
          <span className="text-[11px] font-normal text-slate-400 ml-2">
            external schedulers (cron-job.org / GitHub Actions)
          </span>
        </CardTitle>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded-full hover:bg-slate-100 disabled:opacity-50"
          title="Refresh"
          data-testid="admin-cron-health-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </CardHeader>
      <CardContent>
        {!data ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : crons.length === 0 ? (
          <p className="text-sm text-slate-400">No tracked crons yet.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-cron-health-list">
            {crons.map(c => (
              <div
                key={c.name}
                className="flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-xl p-3"
                data-testid={`admin-cron-row-${c.name}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{c.label}</p>
                  <p className="text-[11px] text-slate-400 font-mono truncate">{c.path}</p>
                  {c.last_error && (
                    <p className="text-[11px] text-red-600 font-mono mt-1 break-words" data-testid={`admin-cron-row-${c.name}-error`}>
                      {c.last_error}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <span
                    className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${pillFor(c.status)}`}
                    data-testid={`admin-cron-row-${c.name}-status`}
                  >
                    {c.status === 'fresh' ? 'on time' : c.status}
                  </span>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Last run: <span className="font-medium text-slate-700">{formatLastRun(c.last_run_at, c.hours_since)}</span>
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Expected every {c.expected_hours >= 24 ? `${Math.round(c.expected_hours / 24)}d` : `${c.expected_hours}h`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-3">
          Rows turn <span className="text-amber-600 font-semibold">amber</span> at 1.25× the expected interval
          and <span className="text-red-600 font-semibold">red</span> at 1.5×. If a cron sits at &quot;never&quot; long after
          you&apos;ve wired it, the cron-job.org schedule isn&apos;t reaching the endpoint — check timezone (UTC vs America/New_York)
          and that the job is enabled.
        </p>

        {/* Verify-in-inbox helpers. Re-send THIS week's/day's email
            template to the admin only, bypassing idempotency locks
            so template tweaks can be eyeballed without DB tinkering. */}
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">

          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Verify the Sunday snapshot</p>
                <p className="text-[11px] text-slate-500">
                  Re-sends this week&apos;s snapshot to your admin email only — other users are not re-emailed.
                </p>
              </div>
              <button
                onClick={sendTestSnapshot}
                disabled={testing}
                className="px-3 py-1.5 rounded-full bg-[#56dfb4] text-[#0b3a2e] text-xs font-bold uppercase tracking-wider hover:bg-[#3fcfa1] disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                data-testid="admin-test-weekly-snapshot-btn"
              >
                {testing ? 'Sending…' : 'Re-send to me'}
              </button>
            </div>
            {testResult && (
              <div
                className={`mt-2 text-[11px] font-mono rounded-lg p-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-100' : 'bg-red-50 text-red-800 border border-red-100'}`}
                data-testid="admin-test-weekly-snapshot-result"
              >
                {testResult.ok ? (
                  <>
                    Sent to <strong>{testResult.to}</strong> · matched events: <strong>{testResult.result?.sent || 0}</strong>
                    {testResult.result?.skipped_no_match > 0 && ' (no in-radius events this week → no email)'}
                  </>
                ) : (
                  <>Error: {testResult.error}</>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Verify the daily new-event alert</p>
                <p className="text-[11px] text-slate-500">
                  Re-sends today&apos;s alert (with a 7-day lookback so there&apos;s always something to preview) to your admin email only.
                </p>
              </div>
              <button
                onClick={sendTestDailyAlert}
                disabled={testingDaily}
                className="px-3 py-1.5 rounded-full bg-[#56dfb4] text-[#0b3a2e] text-xs font-bold uppercase tracking-wider hover:bg-[#3fcfa1] disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                data-testid="admin-test-daily-alerts-btn"
              >
                {testingDaily ? 'Sending…' : 'Re-send to me'}
              </button>
            </div>
            {testResultDaily && (
              <div
                className={`mt-2 text-[11px] font-mono rounded-lg p-2 ${testResultDaily.ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-100' : 'bg-red-50 text-red-800 border border-red-100'}`}
                data-testid="admin-test-daily-alerts-result"
              >
                {testResultDaily.ok ? (
                  <>
                    Sent to <strong>{testResultDaily.to}</strong> · matched events: <strong>{testResultDaily.result?.sent || 0}</strong>
                    {testResultDaily.result?.skipped_no_match > 0 && ' (no in-radius new events → no email)'}
                  </>
                ) : (
                  <>Error: {testResultDaily.error}</>
                )}
              </div>
            )}
          </div>

        </div>
      </CardContent>
    </Card>
  );
}


function EmailHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const load = (d = days) => {
    setLoading(true);
    api.get(`/admin/email-log/summary?days=${d}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(7); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const t = data?.totals || { total: 0, sent: 0, failed: 0, skipped: 0 };
  const failPct = data ? Math.round((data.failure_rate || 0) * 100) : 0;
  const tone =
    !data || t.total === 0 ? 'border-slate-200' :
    failPct >= 10 ? 'border-red-200 bg-red-50/40' :
    failPct >= 3 ? 'border-amber-200 bg-amber-50/30' :
    'border-emerald-200 bg-emerald-50/30';

  return (
    <Card className={`border ${tone} mb-8`} data-testid="admin-email-health-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Inbox className="w-4 h-4 text-[#b456df]" /> Email Health
          <span className="text-[11px] font-normal text-slate-400 ml-2">last {data?.days || days} days</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          {[1, 7, 30].map(d => (
            <button
              key={d}
              onClick={() => { setDays(d); load(d); }}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${days === d ? 'bg-[#b456df] text-white border-[#b456df]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              data-testid={`admin-email-health-range-${d}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
          <button
            onClick={() => load()}
            disabled={loading}
            className="p-1.5 rounded-full hover:bg-slate-100 disabled:opacity-50"
            title="Refresh"
            data-testid="admin-email-health-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!data ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-email-health-totals">
              <div className="rounded-xl bg-white border border-slate-100 p-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Total</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.total}</p>
              </div>
              <div className="rounded-xl bg-white border border-emerald-100 p-3">
                <p className="text-xs text-emerald-600 uppercase tracking-wide flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sent</p>
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.sent}</p>
              </div>
              <div className="rounded-xl bg-white border border-red-100 p-3">
                <p className="text-xs text-red-600 uppercase tracking-wide flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed</p>
                <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.failed}</p>
                <p className="text-[11px] text-slate-400">{failPct}% of total</p>
              </div>
              <div className="rounded-xl bg-white border border-amber-100 p-3">
                <p className="text-xs text-amber-600 uppercase tracking-wide flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Skipped</p>
                <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.skipped}</p>
              </div>
            </div>

            {data.top_errors?.length > 0 && (
              <div data-testid="admin-email-health-top-errors">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Top failure reasons</p>
                <div className="space-y-1.5">
                  {data.top_errors.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs bg-white border border-red-100 rounded-lg p-2">
                      <span className="font-bold text-red-700 flex-shrink-0">×{e.count}</span>
                      <span className="text-slate-700 font-mono break-words">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.top_failed_recipients?.length > 0 && (
              <div data-testid="admin-email-health-top-recipients">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Recipients hit hardest</p>
                <div className="space-y-1">
                  {data.top_failed_recipients.map((r) => (
                    <div key={r.email} className="flex items-center justify-between text-xs bg-white border border-slate-100 rounded-lg p-2">
                      <span className="text-slate-700 truncate">{r.email}</span>
                      <span className="text-red-600 font-medium flex-shrink-0">{r.failures} failure{r.failures === 1 ? '' : 's'}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  Check Resend&apos;s suppression list for these addresses — once suppressed, future sends are silently dropped.
                </p>
              </div>
            )}

            {t.total === 0 && (
              <p className="text-sm text-slate-400 text-center py-2">No email activity in this window.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}


function StatCard({ label, value, icon: Icon, color, sub, link, testId }) {
  const Inner = (
    <Card className="border-slate-100 hover:shadow-md transition-all duration-300 h-full" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        </div>
        <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub ? <p className="text-[11px] text-slate-400 mt-1">{sub}</p> : null}
      </CardContent>
    </Card>
  );
  return link ? <Link to={link}>{Inner}</Link> : <div>{Inner}</div>;
}

export default function AdminDashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const loadAnalytics = () => {
    setRefreshing(true);
    return api.get('/admin/analytics')
      .then(({ data }) => { setAnalytics(data); setFetchedAt(new Date()); })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    loadAnalytics();
    // Auto-refresh every 60 seconds so the user count, pending reports, etc.
    // stay live without the admin having to reload. Paused when the tab is
    // hidden (document.visibilityState) to avoid hammering the API in
    // background tabs.
    const tick = () => {
      if (document.visibilityState === 'visible') loadAnalytics();
    };
    const iv = setInterval(tick, 60_000);
    return () => clearInterval(iv);
  }, []);

  // Merge growth + revenue_growth for a combined chart. Memoized so recharts
  // doesn't re-render the line chart every time an unrelated piece of state
  // (like the GridFS badge "re-check" spinner) updates this page.
  // Must be declared before the early `return` so React's rules-of-hooks
  // sees useMemo called in the same order on every render.
  const combinedGrowth = useMemo(() => {
    if (!analytics) return [];
    const growthMap = {};
    (analytics.growth_data || []).forEach(d => { growthMap[d._id] = { month: d._id, users: d.count, revenue: 0 }; });
    (analytics.revenue_growth || []).forEach(d => {
      growthMap[d._id] = { ...(growthMap[d._id] || { month: d._id, users: 0 }), revenue: d.amount || 0 };
    });
    return Object.values(growthMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [analytics]);

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" />
      </div>
    );
  }

  const overviewStats = [
    { label: 'Total Users', value: analytics.total_users, icon: Users, color: '#56dfb4', link: '/admin/users', testId: 'admin-stat-total-users' },
    { label: 'Active Users', value: analytics.active_users, icon: UserCheck, color: '#56c6df', testId: 'admin-stat-active-users' },
    { label: 'New (30d)', value: analytics.new_users_30d, icon: TrendingUp, color: '#56dfb4', sub: `${analytics.new_users_7d || 0} last 7 days`, testId: 'admin-stat-new-users' },
    { label: 'Total Revenue', value: `$${(analytics.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: '#df5681', sub: `$${(analytics.revenue_30d || 0).toFixed(2)} in 30d`, link: '/admin/subscriptions', testId: 'admin-stat-revenue' },
    { label: 'Pending Reports', value: analytics.pending_reports, icon: Flag, color: '#ef4444', link: '/admin/reports', testId: 'admin-stat-pending-reports' },
    { label: 'Banned Users', value: analytics.banned_users, icon: Users, color: '#64748b', testId: 'admin-stat-banned' },
    { label: 'Hidden Testers', value: analytics.test_accounts || 0, icon: FlaskConical, color: '#d97706', sub: 'Excluded from search', link: '/admin/users', testId: 'admin-stat-test-accounts' },
  ];

  const engagementStats = [
    { label: 'Messages (30d)', value: analytics.messages_30d || 0, icon: MessageSquare, color: '#b456df', sub: `${analytics.total_messages || 0} all-time`, testId: 'admin-stat-messages-30d' },
    { label: 'Conversations', value: analytics.total_conversations || 0, icon: MessagesSquare, color: '#56c6df', testId: 'admin-stat-conversations' },
    { label: 'Upcoming Events', value: analytics.upcoming_events || 0, icon: Calendar, color: '#56dfb4', sub: `${analytics.total_events || 0} total`, testId: 'admin-stat-events' },
    { label: 'Forum Threads', value: analytics.forum_threads || 0, icon: MessagesSquare, color: '#df5681', sub: `${analytics.forum_posts || 0} posts`, testId: 'admin-stat-forum' },
    { label: 'Alert Opt-ins', value: analytics.last_minute_alerts_opted_in || 0, icon: Bell, color: '#f59e0b', sub: 'Last-minute game alerts', testId: 'admin-stat-alerts' },
    { label: 'Public Instructors', value: analytics.instructors_count || 0, icon: GraduationCap, color: '#b456df', testId: 'admin-stat-instructors' },
  ];

  const totalUsers = analytics.total_users || 1;
  const profileCompletionRate = Math.round(((analytics.profile_complete_count || 0) / totalUsers) * 100);
  const withPhotoRate = Math.round(((analytics.profile_with_photo || 0) / totalUsers) * 100);
  const withBioRate = Math.round(((analytics.profile_with_bio || 0) / totalUsers) * 100);

  return (
    <div data-testid="admin-dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Platform analytics &amp; overview
            {fetchedAt && (
              <span className="text-xs text-slate-400 ml-2" data-testid="admin-dashboard-fetched-at">
                · last updated {fetchedAt.toLocaleTimeString()} (auto-refreshes every 60s)
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={loadAnalytics}
            disabled={refreshing}
            className="px-3 py-2 text-xs rounded-full bg-[#56dfb4]/15 hover:bg-[#56dfb4]/25 text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid="admin-dashboard-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
          <Link to="/admin/users" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-users">Users</Link>
          <Link to="/admin/subscriptions" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-subscriptions">Subscriptions</Link>
          <Link to="/admin/events" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-events">Events</Link>
          <Link to="/admin/charity-events" className="px-4 py-2 text-sm rounded-full bg-[#df5681]/10 text-[#df5681] hover:bg-[#df5681]/20 transition-colors" data-testid="admin-nav-charity-events">Charity Queue</Link>
          <Link to="/admin/referrals" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-referrals">Referrals</Link>
          <Link to="/admin/coupons" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-coupons">Coupons</Link>
          <Link to="/admin/sms-log" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-sms-log">SMS Log</Link>
          <Link to="/admin/review-moderation" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-review-moderation">Reviews &amp; Event Reports</Link>
          <Link to="/admin/reports" className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="admin-nav-reports">User Reports</Link>
        </div>
      </div>

      {/* GridFS / image persistence health */}
      <GridfsHealthBadge />

      {/* AWS S3 — post-migration source of truth for new image uploads */}
      <S3HealthBadge />

      {/* Post-deploy smoke test (Mongo + GridFS write + Resend + Twilio + PayPal) */}
      <DeploySmokeBadge />

      {/* Cron health — surfaces external schedulers (cron-job.org / GitHub Actions)
          that have silently stopped firing before users complain. */}
      <CronHealthCard />

      {/* Email send health — catch quota / suppression issues before users complain */}
      <EmailHealthCard />

      {/* Overview Stats */}
      <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {overviewStats.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Engagement Stats */}
      <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Engagement</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {engagementStats.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Growth Chart + Subscription Tiers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="border-slate-100 lg:col-span-2" data-testid="admin-growth-chart">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Growth (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            {combinedGrowth.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={combinedGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="users" name="New Users" stroke="#56dfb4" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#df5681" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-400 py-10">No growth data yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-100" data-testid="admin-tier-breakdown">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscription Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(analytics.tier_stats || {}).map(([tier, count]) => {
                const color = TIER_COLORS[tier] || '#94a3b8';
                const revenue = (analytics.revenue_by_tier || {})[tier] || 0;
                const pct = Math.min(((count / totalUsers) * 100), 100);
                return (
                  <div key={tier}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{tierLabel(tier)}</span>
                      <span className="text-xs text-slate-500">
                        {count} {revenue > 0 ? `· $${revenue.toFixed(2)}` : ''}
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
              {Object.keys(analytics.tier_stats || {}).length === 0 && (
                <p className="text-center text-slate-400 py-6 text-sm">No subscription data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile Completion + Geography */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-100" data-testid="admin-profile-completion">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'Complete profile', pct: profileCompletionRate, count: analytics.profile_complete_count || 0, color: '#56dfb4' },
                { label: 'Has profile photo', pct: withPhotoRate, count: analytics.profile_with_photo || 0, color: '#56c6df' },
                { label: 'Has bio', pct: withBioRate, count: analytics.profile_with_bio || 0, color: '#b456df' },
              ].map(item => (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <span className="text-xs text-slate-500">{item.count} · {item.pct}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100" data-testid="admin-top-zip-codes">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <MapPin className="w-4 h-4 text-[#df5681]" /> Top 20 User Locations
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">Real members only — useful for geo-targeted ad spend. Excludes admins, demo, banned, and test accounts.</p>
          </CardHeader>
          <CardContent>
            {(analytics.top_zip_codes || []).length > 0 ? (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full text-sm" data-testid="admin-top-zip-codes-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="py-2 pl-2 pr-2 font-medium w-8">#</th>
                      <th className="py-2 px-2 font-medium">Location</th>
                      <th className="py-2 px-2 font-medium">ZIP</th>
                      <th className="py-2 pr-2 pl-2 font-medium text-right">Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_zip_codes.map((row, i) => (
                      <tr key={row._id} className="border-b border-slate-50 last:border-b-0" data-testid={`admin-top-zip-row-${row._id}`}>
                        <td className="py-2 pl-2 pr-2 text-slate-400 font-mono">{i + 1}</td>
                        <td className="py-2 px-2">
                          {row.city
                            ? <span>{row.city}{row.state ? `, ${row.state}` : ''}</span>
                            : <span className="text-slate-400 italic">Unknown</span>}
                        </td>
                        <td className="py-2 px-2 font-mono text-slate-600">{row._id}</td>
                        <td className="py-2 pr-2 pl-2 text-right font-medium tabular-nums">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-400 py-10 text-sm">No zip code data yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-100" data-testid="admin-top-states">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <MapPin className="w-4 h-4 text-[#56dfb4]" /> Top States
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(analytics.top_states || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.top_states} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="_id" tick={{ fontSize: 12 }} width={50} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#56dfb4" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-400 py-10 text-sm">No state data yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
